import type { KeyboardEvent } from "react";

/**
 * Inserts text at the caret or supplied range, replacing that selection. Goes through the
 * browser's editing command first so the edit lands on the native undo stack,
 * and otherwise falls back to `setRangeText` plus a synthetic input event.
 */
export function insertTextAtCursor(
  textarea: HTMLTextAreaElement,
  text: string,
  range = { start: textarea.selectionStart, end: textarea.selectionEnd },
): void {
  if (textarea.readOnly || textarea.disabled) return;
  textarea.setSelectionRange(range.start, range.end);
  if (insertTextViaEditingCommand(textarea, text)) return;
  textarea.setRangeText(text, range.start, range.end, "end");
  // setRangeText bypasses React's value tracker, which is what lets this
  // dispatched event register as a change on a controlled textarea.
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
}

export function insertNewlineAtCursor(textarea: HTMLTextAreaElement): void {
  insertTextAtCursor(textarea, "\n");
}

/**
 * `execCommand` is deprecated, but it is the only way to edit a textarea that
 * the browser records for undo, and it fires the input event itself. It acts
 * on the focused editable, so it is only attempted when that is this textarea.
 * Returns false when the browser lacks or declines the command.
 */
function insertTextViaEditingCommand(
  textarea: HTMLTextAreaElement,
  text: string,
): boolean {
  const doc = textarea.ownerDocument;
  if (typeof doc.execCommand !== "function") return false;
  if (doc.activeElement !== textarea) return false;
  try {
    return doc.execCommand("insertText", false, text);
  } catch {
    return false;
  }
}

/**
 * Option/Alt+Enter inserts a line break, matching the convention of other
 * LLM chat clients where plain Enter sends. Browsers disagree on a default
 * for it in a textarea (Chrome ignores it, Firefox breaks the line), so the
 * break is inserted here and the default suppressed. Returns true when the
 * event was handled so callers can skip their submit path.
 */
export function handleAltEnterNewline(
  event: KeyboardEvent<HTMLTextAreaElement>,
): boolean {
  if (event.key !== "Enter" || !event.altKey) return false;
  event.preventDefault();
  insertNewlineAtCursor(event.currentTarget);
  return true;
}
